import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LogOut, Globe, Map, Layers, Activity, Calculator, Database } from "lucide-react";
import "./noctua.css";
import { NoctuaContext } from "./NoctuaContext";

const TABS = [
  { to: "/noctua/mapa", icon: Map, label: "Mapa Electoral", testid: "noctua-tab-mapa" },
  { to: "/noctua/lectura", icon: Layers, label: "Lectura Territorial", testid: "noctua-tab-lectura" },
  { to: "/noctua/inteligencia", icon: Activity, label: "Panel de Inteligencia", testid: "noctua-tab-inteligencia" },
  { to: "/noctua/escenarios", icon: Calculator, label: "Motor de Escenarios", testid: "noctua-tab-escenarios" },
  { to: "/noctua/datos", icon: Database, label: "Datos", testid: "noctua-tab-datos" },
];

export default function NoctuaShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [partidos, setPartidos] = useState([]);
  const [clientParty, setClientParty] = useState(null);
  const [allStates, setAllStates] = useState([]);
  const [selectedState, setSelectedStateRaw] = useState(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("noctua.selectedState") : null;
    return stored ? parseInt(stored, 10) : 8;
  });

  const setSelectedState = (v) => {
    const n = typeof v === "number" ? v : parseInt(v, 10);
    if (!Number.isNaN(n)) {
      setSelectedStateRaw(n);
      try { window.localStorage.setItem("noctua.selectedState", String(n)); } catch (e) { /* ignore */ }
    }
  };

  const loadPartidos = async () => {
    const r = await api.get("/noctua/partidos");
    setPartidos(r.data);
    const c = r.data.find((p) => p.is_client);
    if (c) setClientParty(c.clave);
  };

  useEffect(() => {
    loadPartidos();
    api.get("/catalog/states").then((r) => setAllStates(r.data)).catch(() => {});
  }, []);

  const setClient = async (clave) => {
    await api.post(`/noctua/partidos/${clave}/set-client`);
    setClientParty(clave);
    loadPartidos();
  };

  return (
    <NoctuaContext.Provider value={{
      partidos, clientParty, refreshPartidos: loadPartidos, setClient,
      selectedState, setSelectedState, allStates,
    }}>
      <div className="noctua-shell noctua-bg" data-testid="noctua-shell">
        {/* Top bar */}
        <header className="border-b border-slate-800/60 bg-slate-950/60 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 grid place-items-center rounded bg-gradient-to-br from-blue-500 to-blue-700">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-display font-black text-white text-base leading-none tracking-tight">
                  NOCTUA
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">
                  Inteligencia Electoral · Terreno Político
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 mr-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Estado</span>
                <Select value={String(selectedState)} onValueChange={setSelectedState}>
                  <SelectTrigger className="w-44 h-8 bg-slate-900 border-slate-700 text-white text-xs" data-testid="noctua-global-state-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 max-h-80">
                    {allStates.map((s) => (
                      <SelectItem key={s.clave} value={String(s.clave)} className="text-white text-xs" data-testid={`noctua-state-${s.clave}`}>
                        {String(s.clave).padStart(2, "0")} · {s.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="hidden md:block text-right">
                <div className="text-xs text-slate-400">Sesión</div>
                <div className="text-sm font-medium text-white">{user?.name}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                onClick={() => { logout(); nav("/"); }}
                data-testid="noctua-logout"
              >
                <LogOut className="w-4 h-4 mr-2" /> Salir
              </Button>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="border-b border-slate-800/60">
          <div className="max-w-7xl mx-auto px-6 flex gap-8 overflow-x-auto">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                className={({ isActive }) => `noctua-tab flex items-center gap-2 whitespace-nowrap ${isActive ? "active" : ""}`}
                data-testid={t.testid}
              >
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-6 py-8">
          <Outlet />
        </main>

        {/* Bottom party selector */}
        <div className="fixed bottom-0 inset-x-0 z-30 bg-slate-950/90 backdrop-blur-md border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between gap-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
              Partido cliente activo
            </div>
            <div className="flex items-center gap-3">
              {clientParty && (
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: partidos.find((p) => p.clave === clientParty)?.color }}
                />
              )}
              <Select value={clientParty || ""} onValueChange={setClient}>
                <SelectTrigger className="w-48 h-9 bg-slate-900 border-slate-700 text-white" data-testid="noctua-party-select">
                  <SelectValue placeholder="Selecciona partido" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {partidos.map((p) => (
                    <SelectItem key={p.clave} value={p.clave} className="text-white" data-testid={`noctua-party-${p.clave}`}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                        {p.clave}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="h-14" /> {/* spacer for fixed bottom */}
      </div>
    </NoctuaContext.Provider>
  );
}
